import {
  Anchor,
  Box,
  Collapse,
  Flex,
  Group,
  Stack,
  Table,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import dayjs from 'dayjs';
import { type ReactNode, useState } from 'react';
import {
  PiArrowDown,
  PiArrowRight,
  PiCaretDown,
  PiCaretRight,
} from 'react-icons/pi';
import { Link } from 'wouter';
import { money } from '@/components/InvoiceDetailsModal/DocumentCard';
import type {
  TDocumentGroup,
  TGroupFinalInvoice,
  TGroupPlan,
  TGroupProforma,
} from '@/models/invoices';

const day = (v: string | null | undefined) =>
  v ? dayjs(v).format('DD.MM.YYYY') : '';

function Node({
  to,
  current,
  struck,
  title,
  sub,
  maxWidth,
}: {
  to?: string;
  current?: boolean;
  struck?: boolean;
  title: ReactNode;
  sub?: ReactNode;
  maxWidth?: number;
}) {
  const body = (
    <Box
      px={8}
      py={3}
      style={{
        display: 'inline-block',
        maxWidth,
        borderRadius: 6,
        background: current
          ? 'var(--mantine-color-blue-light)'
          : 'var(--mantine-color-body)',
        border: current
          ? '1px solid var(--mantine-color-blue-6)'
          : '1px solid var(--mantine-color-default-border)',
        boxShadow: current
          ? 'inset 0 0 0 1px var(--mantine-color-blue-6)'
          : undefined,
        opacity: struck ? 0.55 : 1,
      }}
    >
      <Text fz="xs" fw={600} td={struck ? 'line-through' : undefined}>
        {title}
      </Text>
      {sub && (
        <Text fz={11} c="dimmed">
          {sub}
        </Text>
      )}
    </Box>
  );
  return to && !current ? (
    <Anchor component={Link} to={to} underline="never" c="inherit">
      {body}
    </Anchor>
  ) : (
    body
  );
}

/** Whether the client was sent the document as it stands: not sent in orange. */
const SentMark = ({ sent }: { sent: boolean }) => (
  <>
    {' · '}
    {sent ? (
      'sent'
    ) : (
      <Text span inherit c="orange">
        not sent
      </Text>
    )}
  </>
);

const Arrow = ({ down }: { down?: boolean }) =>
  down ? (
    <PiArrowDown size={12} color="var(--mantine-color-gray-5)" />
  ) : (
    <PiArrowRight size={12} color="var(--mantine-color-gray-5)" />
  );

export function ProformaNode({
  p,
  compact,
}: {
  p: TGroupProforma;
  compact?: boolean;
}) {
  return (
    <Node
      to={`~/invoices/${p.id}`}
      current={p.current}
      struck={p.status === 'cancelled'}
      title={compact ? p.number : `Proforma ${p.number}`}
      sub={
        <>
          v{p.documentVersion} · {p.state}
          {p.status === 'issued' ? '' : ` ${day(p.stateChangedAt)}`}
          {p.status !== 'cancelled' && <SentMark sent={p.sent} />}
        </>
      }
    />
  );
}

function InvoiceNode({
  f,
  compact,
}: {
  f: TGroupFinalInvoice;
  compact?: boolean;
}) {
  return (
    <Node
      to={`~/invoices/final/${f.id}`}
      current={f.current}
      title={compact ? f.number : `Invoice ${f.number}`}
      sub={
        <>
          issued {day(f.issuedAt)}
          <SentMark sent={f.sent} />
        </>
      }
    />
  );
}

const Placeholder = ({ children }: { children: ReactNode }) => (
  <Text fz="xs" c="dimmed" fs="italic">
    {children}
  </Text>
);

function invoiceCell(proformas: TGroupProforma[], compact = false) {
  const settled = proformas.find((p) => p.finalInvoice);
  if (settled?.finalInvoice) {
    return <InvoiceNode f={settled.finalInvoice} compact={compact} />;
  }
  if (proformas.some((p) => p.status === 'paid')) {
    return <Placeholder>Settled before final invoices existed</Placeholder>;
  }
  if (proformas.some((p) => p.status === 'issued')) {
    return (
      <Placeholder>
        {compact ? 'after settle' : 'Invoice: after settle'}
      </Placeholder>
    );
  }
  return <Placeholder>-</Placeholder>;
}

function OfferNode({ group }: { group: TDocumentGroup }) {
  if (!group.offer) return <Placeholder>No offer</Placeholder>;
  return (
    <Node
      to={`~/offers/${group.offer.id}/general`}
      title="Offer"
      maxWidth={170}
      sub={group.offer.title ?? `#${group.offer.id}`}
    />
  );
}

/**
 * An installment's money in the row: what its live proforma asks for, in that proforma's currency;
 * before it is issued, its agreed amount in the plan's base currency (never its payment currency).
 */
function itemMoney(item: TGroupPlan['items'][number], base: string) {
  const live = item.proformas.findLast((p) => p.status !== 'cancelled');
  const due = live ? live.dueAmount : item.agreed;
  return {
    live,
    due,
    currency: live ? live.dueCurrency : base,
    collected: live?.collected ?? 0,
  };
}

function DueAmount({
  item,
  base,
}: {
  item: TGroupPlan['items'][number];
  base: string;
}) {
  const { due, currency, collected } = itemMoney(item, base);
  return (
    <Text fz={11} c="dimmed">
      {collected > 0 && (
        <Text
          span
          inherit
          fw={600}
          c={due != null && collected >= due ? 'teal' : 'orange'}
        >
          {money(collected, null)} /{' '}
        </Text>
      )}
      {due != null ? money(due, currency) : ''}
    </Text>
  );
}

function Proformas({ item }: { item: TGroupPlan['items'][number] }) {
  const latest = item.proformas.at(-1);
  if (!latest) return <Placeholder>not issued yet</Placeholder>;
  return <ProformaNode p={latest} compact />;
}

const planBox = {
  borderRadius: 6,
  background: 'var(--mantine-color-body)',
  border: '1px solid var(--mantine-color-default-border)',
};

/** The plan item by item, one block each, for a column too narrow for the table. */
function PlanList({ plan }: { plan: TGroupPlan }) {
  return (
    <Stack gap={0} style={planBox}>
      {plan.items.map((item, i) => {
        const { live } = itemMoney(item, plan.baseCurrency ?? 'EUR');
        return (
          <Stack
            key={item.id}
            gap={4}
            px={8}
            py={6}
            style={{
              borderBottom:
                i < plan.items.length - 1
                  ? '1px solid var(--mantine-color-default-border)'
                  : undefined,
            }}
          >
            <Group gap={6} justify="space-between" wrap="nowrap">
              <Text fz="xs" fw={600}>
                #{item.seq} · {live ? day(live.dueAt) : 'not set yet'}
              </Text>
              <DueAmount item={item} base={plan.baseCurrency ?? 'EUR'} />
            </Group>
            <Group gap={6} wrap="wrap" align="center">
              <Proformas item={item} />
              {item.proformas.length > 0 && <Arrow />}
              {invoiceCell(item.proformas, true)}
            </Group>
          </Stack>
        );
      })}
    </Stack>
  );
}

function PlanTable({ plan, narrow }: { plan: TGroupPlan; narrow?: boolean }) {
  if (narrow) return <PlanList plan={plan} />;
  return (
    <Box px={6} style={planBox}>
      <Table verticalSpacing={3} horizontalSpacing={8} fz="xs" withRowBorders>
        <Table.Thead>
          <Table.Tr style={{ whiteSpace: 'nowrap' }}>
            <Table.Th w={20}>#</Table.Th>
            <Table.Th>Due</Table.Th>
            <Table.Th>Proforma</Table.Th>
            <Table.Th>Invoice</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {plan.items.map((item) => {
            const { live } = itemMoney(item, plan.baseCurrency ?? 'EUR');
            return (
              <Table.Tr key={item.id}>
                <Table.Td>{item.seq}</Table.Td>
                <Table.Td style={{ whiteSpace: 'nowrap' }}>
                  <Text fz="xs">{live ? day(live.dueAt) : 'not set yet'}</Text>
                  <DueAmount item={item} base={plan.baseCurrency ?? 'EUR'} />
                </Table.Td>
                <Table.Td>
                  <Proformas item={item} />
                </Table.Td>
                <Table.Td>{invoiceCell(item.proformas, true)}</Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Box>
  );
}

function Collapsible({
  label,
  defaultOpen = false,
  children,
}: {
  label: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box>
      <UnstyledButton onClick={() => setOpen((o) => !o)}>
        <Group gap={4}>
          {open ? <PiCaretDown size={12} /> : <PiCaretRight size={12} />}
          <Text fz="xs" c="dimmed">
            {label}
          </Text>
        </Group>
      </UnstyledButton>
      <Collapse expanded={open}>
        <Box pt={6}>{children}</Box>
      </Collapse>
    </Box>
  );
}

const Related = ({ children }: { children: ReactNode }) => (
  <Box p="xs" bg="var(--mantine-color-gray-light)" style={{ borderRadius: 6 }}>
    {children}
  </Box>
);

/**
 * Every related document, linked, on a grey band above the document (spec 9.2), as stages left to right
 * (top to bottom when the card is narrow): the offer, the split plan when there is one, then the
 * proformas and invoice, or the plan table row by row (item by item in a narrow column). Cancelled
 * documents are dimmed and struck; proformas outside the plan (cancelled before it) and plans
 * undone by Unlock are folded away.
 */
const WIDE = 1000;
const TABLE = 560;

export function ChainStrip({ group }: { group: TDocumentGroup }) {
  const { ref, width } = useElementSize();
  // Unmeasured (the first frame) reads as the narrow column it sits in, so it never flashes a row.
  const wide = width >= WIDE;
  const narrow = width < TABLE;

  if (group.excluded === 'renewal') {
    return (
      <Related>
        <Group gap={6} align="center">
          <OfferNode group={group} />
          <Arrow />
          {group.proformas.map((p) => (
            <ProformaNode key={p.id} p={p} />
          ))}
          <Placeholder>
            Renewal document - not part of the order's chain
          </Placeholder>
        </Group>
      </Related>
    );
  }

  const plan = group.plan;
  const step = (
    <Box pt={wide ? 8 : 0} pl={wide ? 0 : 12}>
      <Arrow down={!wide} />
    </Box>
  );
  const last = {
    flex: wide ? 1 : undefined,
    minWidth: 0,
    width: wide ? undefined : '100%',
  };
  return (
    <Related>
      <Flex
        ref={ref}
        direction={wide ? 'row' : 'column'}
        gap={8}
        align="flex-start"
      >
        <OfferNode group={group} />
        {step}
        {plan ? (
          <>
            <Node
              to={
                group.offer ? `~/offers/${group.offer.id}/finance` : undefined
              }
              title="Split plan"
              maxWidth={170}
              sub={`${plan.status === 'committed' ? 'in force' : plan.status}${
                plan.committedAt ? ` · approved ${day(plan.committedAt)}` : ''
              } · ${plan.count} installments`}
            />
            {step}
            <Stack gap="xs" style={last}>
              <PlanTable plan={plan} narrow={narrow} />
              {group.proformas.length > 0 && (
                <Collapsible
                  defaultOpen={group.proformas.some((p) => p.current)}
                  label={`${group.proformas.length} proforma${
                    group.proformas.length === 1 ? '' : 's'
                  } outside the plan`}
                >
                  <Group gap={6}>
                    {group.proformas.map((p) => (
                      <ProformaNode key={p.id} p={p} />
                    ))}
                  </Group>
                </Collapsible>
              )}
              {group.pastPlans.map((p) => (
                <PastPlan key={p.id} plan={p} narrow={narrow} />
              ))}
            </Stack>
          </>
        ) : (
          <Stack gap="xs" style={last}>
            <Group gap={6} wrap="wrap" align="center">
              {group.proformas.map((p, i) => (
                <Group key={p.id} gap={6} wrap="nowrap">
                  {i > 0 && <Arrow />}
                  <ProformaNode p={p} />
                </Group>
              ))}
              {group.proformas.length > 0 && <Arrow />}
              {invoiceCell(group.proformas)}
            </Group>
            {group.pastPlans.map((p) => (
              <PastPlan key={p.id} plan={p} />
            ))}
          </Stack>
        )}
      </Flex>
    </Related>
  );
}

function PastPlan({ plan, narrow }: { plan: TGroupPlan; narrow?: boolean }) {
  return (
    <Collapsible
      defaultOpen={plan.items.some((i) =>
        i.proformas.some((x) => x.current || x.finalInvoice?.current),
      )}
      label={`Earlier plan, ${plan.status} ${day(plan.updatedAt)} (${plan.count} installments)`}
    >
      <PlanTable plan={plan} narrow={narrow} />
    </Collapsible>
  );
}
